import { motion } from "framer-motion";
import CountUp from "react-countup";
import { ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { STATS, STATS_VERIFIED_ON, CHANNEL_ABOUT_URL, CHANNEL_URL } from "@/data/site";

// Preview: backend URL assoluto da .env. Pacchetto statico: percorso relativo
// alla sottocartella di installazione (PUBLIC_URL), così le chiamate restano same-origin.
const API = process.env.REACT_APP_BACKEND_URL || process.env.PUBLIC_URL || "";

export default function Numbers() {
  const [live, setLive] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/youtube/stats`)
      .then((r) => r.json())
      .then((d) => { if (d && d.live) setLive(d); })
      .catch(() => {});
  }, []);

  const values = {
    iscritti: live ? live.subscribers : null,
    visualizzazioni: live ? live.views : null,
    video: live ? live.videos : null,
  };
  const updatedLabel = live
    ? `Dati aggiornati in automatico · ${new Date(live.updated_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}`
    : `Dati aggiornati al ${STATS_VERIFIED_ON}`;
  return (
    <section id="pubblico" data-testid="numbers-section" className="relative py-20 md:py-32 bg-[#f7f5f0] border-t border-black/[0.06]">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-8 mb-14 md:mb-20">
          <div className="lg:col-span-4">
            <div className="label-eyebrow mb-4 flex items-center gap-3">
              <span>I.</span>
              <span className="w-5 h-px bg-black/25" />
              <span>Il pubblico</span>
            </div>
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-display tighter text-[#121212] text-4xl md:text-5xl lg:text-[3.8vw] leading-[1.02] font-normal">
              Un canale <span className="italic text-[#FF6B1A]">in crescita</span>,
              <br />
              con una community verticale.
            </h2>
            <p className="mt-6 text-[#121212]/55 text-sm max-w-xl leading-relaxed">
              I numeri si riferiscono al canale YouTube Modo Motori.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {STATS.map((s, i) => (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.07 }}
              data-testid={`stat-${s.key}`}
              className="border-t-2 border-[#121212] pt-6"
            >
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#121212]/50 mb-4">
                {s.label}
              </div>
              <div className="font-display text-[#121212] text-5xl md:text-6xl lg:text-7xl tighter leading-none">
                <CountUp end={values[s.key] ?? s.value} duration={2.4} separator="." enableScrollSpy scrollSpyOnce useEasing />
              </div>
            </motion.div>
          ))}
        </div>

        <div data-testid="numbers-editorial" className="mt-16 md:mt-20 max-w-4xl">          <p className="font-display text-[#121212] text-2xl md:text-3xl lg:text-[2.1vw] leading-[1.25] font-normal">
            <span className="float-left font-display text-[#FF6B1A] text-7xl md:text-8xl leading-[0.75] pr-4 pt-2">M</span>
            odo Motori è la tua guida alla scelta della prossima auto: dalle ultime <span className="italic">novità di mercato</span> ai <span className="italic">test drive</span>, dai confronti tra modelli alle innovazioni tecnologiche e alle nuove soluzioni per la mobilità.
          </p>
          <p className="mt-7 text-[#121212]/65 text-base md:text-lg leading-relaxed max-w-2xl">
            Analizziamo i motori e le tecnologie di domani con un linguaggio semplice, tecnico quanto basta ma sempre accessibile, per aiutarti a conoscere le alternative, valutare le caratteristiche di ogni modello e trovare l'auto più adatta alle tue esigenze, tenendoti al tempo stesso aggiornato sulle novità e le tendenze del mondo automotive.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-[11px] tracking-[0.18em] uppercase text-[#121212]/70">
            {["Il Nostro Garage", "Colpo di Clacson", "Focus", "Guida Sicura", "Showcase & Lifestyle"].map((f, i, arr) => (
              <span key={f} className="inline-flex items-center gap-3">
                <span>{f}</span>
                {i < arr.length - 1 && <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B1A]" />}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-6 border-t border-black/[0.08]">
          <p className="font-mono text-[11px] tracking-[0.14em] uppercase text-[#121212]/45">
            {updatedLabel}
          </p>
          <a
            href={CHANNEL_ABOUT_URL}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="stats-source-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-[#121212]/60 hover:text-[#FF6B1A] transition-colors"
          >
            Fonte: YouTube · @modo-motori <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </section>
  );
}
